<template>
  <div class="coherence-analysis-tab" style="overflow: visible;">
    <AnalysisProgress
      v-if="isAnalyzing"
      :progress="analysisProgress"
      :current-step="analysisCurrentStep"
      :is-analyzing="isAnalyzing"
      :status="analysisRunStatus"
      :run-id="analysisRunId"
      :skipped-stages="analysisSkippedStages"
      class="mb-3"
      @cancelled="onCancelRequested"
    />

    <EngineEmptyState
      v-if="coherenceSkipReason && !hideCoherence && !isAnalyzing"
      engine="coherence"
      label="coherence"
      :skip-reason="coherenceSkipReason"
      class="my-4 coherence-engine-off"
      @launch="$emit('launch-engine', $event)"
    />

    <div
      v-if="showRunHeader"
      class="run-header d-flex align-center flex-wrap mb-2"
      data-testid="run-header"
    >
      <RunSelector
        v-if="runHistoryEnabled"
        class="mr-2"
        :runs="runs"
        :current-run="currentRun"
        :current-run-id="runId"
        :loading="runsLoading"
        :busy="runActionBusy"
        :read-only="runControlsReadOnly"
        @select="onSelectRun"
        @promote="onPromoteRun"
        @pin="onPinRun"
        @unpin="onUnpinRun"
        @replay="onReplayRun"
      />
      <RunStatusChip
        v-if="currentRun && currentRun.status"
        :status="currentRun.status"
        x-small
        class="mr-2 mb-1"
      />
      <RunVersionChips :run="currentRun" :source-type="analysisSourceType" dense />
      <v-btn
        v-if="runHistoryEnabled && currentRun"
        x-small
        text
        class="text-none ml-1"
        data-testid="toggle-version-details"
        @click="showVersionDetails = !showVersionDetails"
      >
        <v-icon x-small left>{{ showVersionDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        Version details
      </v-btn>
      <span v-if="runActionError" class="caption error--text ml-2">{{ runActionError }}</span>
    </div>

    <template v-if="runHistoryEnabled && currentRun">
      <RunSummaryCards
        class="mb-2"
        :run="currentRun"
        :gestures="currentGestures"
        :previous-run="previousRun"
        :previous-gestures="previousGestures"
        :previous-loading="previousRunLoading"
        :incoherence="incoherence"
        :score-mode="coherenceScoreMode"
      />
      <RunVersionDetailPanel
        v-if="showVersionDetails"
        class="mb-3"
        :run="currentRun"
        :gestures="currentGestures"
        :previous-run="previousRun"
        :previous-gestures="previousGestures"
      />
    </template>

    <div ref="cinemaScope">
      <v-row class="main-layout-row">
        <v-col cols="12" class="coherence-main-col">
          <v-card class="video-card coherence-video-card" elevation="1">
            <v-alert
              v-if="reportMissingBannerActive && $vuetify.breakpoint.mdAndUp"
              dense
              prominent
              type="warning"
              border="left"
              class="report-missing-banner ma-0"
              icon="mdi-flag-plus-outline"
            >
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <strong>Click the moment in the video where you saw an undetected gesture.</strong>
                  <div class="caption">After clicking the timeline, you’ll be asked to complete the report.</div>
                </div>
                <v-btn small text @click="cancelReportMissing">Cancel</v-btn>
              </div>
            </v-alert>
            <v-alert
              v-if="incoherencePickActive && !hideCoherence && $vuetify.breakpoint.mdAndUp"
              dense
              prominent
              type="error"
              border="left"
              class="report-missing-banner ma-0"
              icon="mdi-drama-masks"
            >
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <strong>{{ incoherencePickHint }}</strong>
                  <div class="caption">Click the video timeline twice: lie start, then lie end. Esc cancels.</div>
                </div>
                <v-btn small text @click="cancelIncoherencePick">Cancel</v-btn>
              </div>
            </v-alert>
            <div ref="videoContainerWrapper" class="video-container-wrapper">
              <VideoPlayer
                ref="videoPlayer"
                :analysis-id="interviewId"
                :video-url="videoUrl"
                :gesture-data="gestureData"
                :coherence-timeline-alerts="hideCoherence ? [] : coherenceTimelineAlertsBySemanticsMode"
                :coherence-timeline-blocks="hideCoherence ? [] : coherenceTimelineBlocksBySemanticsMode"
                :coherence-semantic-modes="hideCoherence ? null : coherenceSemanticModes"
                :coherence-score-mode="coherenceScoreMode"
                :coherence-segmentation-tab="coherenceSegmentationTab"
                :semaphore-threshold-good="coherenceSemaphoreThresholds.good"
                :semaphore-threshold-warn="coherenceSemaphoreThresholds.warn"
                :show-landmarks="shouldShowLandmarks"
                :is-analyzing="isAnalyzing"
                :analysis-progress="analysisProgress"
                :analysis-step="analysisCurrentStep"
                :auto-pause-on-red-alert="autoPauseOnRedAlert"
                :landmarks-toggle="landmarksToggleOptions"
                :fill-container-width="true"
                :is-manual-analysis="!!analysisData && !!analysisData.isManualAnalysis"
                analysis-view-mode="coherence"
                :show-view-mode-toggle="false"
                :show-overlay-toggle="false"
                :show-footer-settings-menu="false"
                :overlays-visible="overlaysVisible"
                :report-missing-active="reportMissingBannerActive || incoherencePickActive"
                :incoherence-range-ms="hideCoherence ? null : incoherenceRangeMs"
                @timeupdate="onVideoTimeUpdate"
                @toggle-overlays="onToggleOverlays"
                @pick-timestamp="onVideoPickTimestamp"
              >
                <template #overlay>
                  <div
                    v-if="overlaysVisible && !isMobileLayout"
                    class="coherence-in-video-overlays"
                    :class="{
                      'coherence-in-video-overlays--manual': candidateInfo.isManualAnalysis,
                    }"
                  >
                    <div
                      class="vio-col-left"
                      :class="{ 'vio-col-left--collapsed': gestureTimelineCollapsed }"
                    >
                      <GestureAlertsTimeline
                        ref="gestureAlertsTimeline"
                        :gesture-data="gestureData"
                        :video-duration="videoDuration"
                        :current-video-time="currentVideoTime"
                        :facial-landmarks-enabled="showLandmarks.face"
                        :hand-landmarks-enabled="showLandmarks.hands"
                        :pause-on-red-alerts-enabled="autoPauseOnRedAlert"
                        :current-only="false"
                        :pick-mode-enabled="timestampPickActive"
                        :source-type="candidateInfo.isManualAnalysis ? 'manual_analysis' : 'interview'"
                        :source-id="sourceId"
                        :collapsed.sync="gestureTimelineCollapsed"
                        @update:facialLandmarksEnabled="onFacialLandmarksSwitch"
                        @update:handLandmarksEnabled="onHandLandmarksSwitch"
                        @update:pauseOnRedAlertsEnabled="onPauseOnRedAlertsSwitch"
                        @jump-to-time="onJumpToTime"
                        @pick-timestamp="onPickTimestamp"
                        @report-missing-start="onReportMissingStart"
                        @report-missing-captured="reportMissingBannerActive = false"
                        @report-missing-saved="onReportMissingSaved"
                        @report-missing-cancel="reportMissingBannerActive = false"
                      />
                    </div>
                    <div
                      v-if="!hideCoherence"
                      class="vio-col-right"
                      :class="{ 'vio-col-right--collapsed': nextSemanticaCollapsed }"
                    >
                      <CoherenceSemanticsPanel
                        ref="coherenceSemanticsPanel"
                        :semantic-modes="coherenceSemanticModes"
                        :coherence-alerts="coherenceTimelineAlertsBySemanticsMode"
                        :interview-id="interviewId"
                        :is-manual-analysis="!!candidateInfo.isManualAnalysis"
                        :current-video-time="currentVideoTime"
                        :segmentation-tab.sync="coherenceSegmentationTab"
                        :score-mode.sync="coherenceScoreMode"
                        :font-scale.sync="nextSemanticaFontScale"
                        :semaphore-threshold-good="coherenceSemaphoreThresholds.good"
                        :semaphore-threshold-warn="coherenceSemaphoreThresholds.warn"
                        :current-only="false"
                        :collapsed.sync="nextSemanticaCollapsed"
                        :incoherence-controls-enabled="!readOnly && !hideCoherence"
                        :incoherence-range-ms="incoherenceRangeMs"
                        :incoherence-label-type="incoherenceLabelType"
                        :incoherence-pick-active="incoherencePickActive"
                        :incoherence-busy="incoherenceLoading || incoherenceSaving"
                        @jump-to-time="onJumpToTime"
                        @request-timeline-timestamp="timestampPickActive = true"
                        @feedback-saved="loadFeedbackSummary"
                        @incoherence-edit-start="startIncoherenceEdit"
                        @incoherence-pick-cancel="cancelIncoherencePick"
                        @incoherence-mark-truthful="markIncoherenceTruthful"
                        @incoherence-clear="clearIncoherence"
                      />
                    </div>
                  </div>
                  <div v-else-if="showMobileNextInVideo" class="coherence-mobile-next-in-video-overlay">
                    <div class="coherence-mobile-next-stack">
                      <CoherenceSemanticsPanel
                        v-if="!hideCoherence"
                        :semantic-modes="coherenceSemanticModes"
                        :coherence-alerts="coherenceTimelineAlertsBySemanticsMode"
                        :interview-id="interviewId"
                        :is-manual-analysis="!!candidateInfo.isManualAnalysis"
                        :current-video-time="currentVideoTime"
                        :segmentation-tab.sync="coherenceSegmentationTab"
                        :score-mode.sync="coherenceScoreMode"
                        :font-scale.sync="nextSemanticaFontScale"
                        :semaphore-threshold-good="coherenceSemaphoreThresholds.good"
                        :semaphore-threshold-warn="coherenceSemaphoreThresholds.warn"
                        :compact-lyrics-viewport="true"
                        :collapsed.sync="nextSemanticaCollapsed"
                        @jump-to-time="onJumpToTime"
                      />
                      <div class="coherence-mobile-gestures-chrome pt-1">
                        <GestureAlertsTimeline
                          :gesture-data="gestureData"
                          :video-duration="videoDuration"
                          :current-video-time="currentVideoTime"
                          :facial-landmarks-enabled="showLandmarks.face"
                          :hand-landmarks-enabled="showLandmarks.hands"
                          :pause-on-red-alerts-enabled="autoPauseOnRedAlert"
                          :current-only="true"
                          :source-type="candidateInfo.isManualAnalysis ? 'manual_analysis' : 'interview'"
                          :source-id="sourceId"
                          :collapsed.sync="gestureTimelineCollapsed"
                          @update:facialLandmarksEnabled="onFacialLandmarksSwitch"
                          @update:handLandmarksEnabled="onHandLandmarksSwitch"
                          @update:pauseOnRedAlertsEnabled="onPauseOnRedAlertsSwitch"
                          @jump-to-time="onJumpToTime"
                        />
                      </div>
                    </div>
                  </div>
                </template>
                <template v-if="overlaysVisible && isMobileLayout" #below-video>
                  <div v-if="showMobileNextBelowVideo && !hideCoherence" class="coherence-mobile-next-stack px-2 pt-2 pb-1">
                    <CoherenceSemanticsPanel
                      :semantic-modes="coherenceSemanticModes"
                      :coherence-alerts="coherenceTimelineAlertsBySemanticsMode"
                      :interview-id="interviewId"
                      :is-manual-analysis="!!candidateInfo.isManualAnalysis"
                      :current-video-time="currentVideoTime"
                      :segmentation-tab.sync="coherenceSegmentationTab"
                      :score-mode.sync="coherenceScoreMode"
                      :font-scale.sync="nextSemanticaFontScale"
                      :semaphore-threshold-good="coherenceSemaphoreThresholds.good"
                      :semaphore-threshold-warn="coherenceSemaphoreThresholds.warn"
                      :compact-lyrics-viewport="true"
                      :collapsed.sync="nextSemanticaCollapsed"
                      @jump-to-time="onJumpToTime"
                    />
                  </div>
                  <div v-if="showMobileNextBelowVideo" class="coherence-mobile-gestures-chrome px-2 pt-1 pb-0">
                    <GestureAlertsTimeline
                      :gesture-data="gestureData"
                      :video-duration="videoDuration"
                      :current-video-time="currentVideoTime"
                      :facial-landmarks-enabled="showLandmarks.face"
                      :hand-landmarks-enabled="showLandmarks.hands"
                      :pause-on-red-alerts-enabled="autoPauseOnRedAlert"
                      :current-only="true"
                      :source-type="candidateInfo.isManualAnalysis ? 'manual_analysis' : 'interview'"
                      :source-id="sourceId"
                      :collapsed.sync="gestureTimelineCollapsed"
                      @update:facialLandmarksEnabled="onFacialLandmarksSwitch"
                      @update:handLandmarksEnabled="onHandLandmarksSwitch"
                      @update:pauseOnRedAlertsEnabled="onPauseOnRedAlertsSwitch"
                      @jump-to-time="onJumpToTime"
                    />
                  </div>
                </template>
              </VideoPlayer>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row
        v-if="!candidateInfo.isManualAnalysis"
        class="interview-metadata-row"
      >
        <v-col cols="12">
          <v-card class="candidate-overlay-card" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-start mb-2 candidate-header-row">
                <div class="d-flex flex-column align-center flex-shrink-0 mr-3">
                  <v-avatar :size="64" :color="headerTotalScoreChipColor">
                    <span class="white--text subtitle-1 font-weight-bold candidate-total-circle-text">
                      {{ candidateInfo.headerTotalScoreLabel || 'N/A' }}
                    </span>
                  </v-avatar>
                </div>
                <div class="flex-grow-1 pt-1" style="min-width: 0;">
                  <div class="subtitle-2 font-weight-bold">{{ candidateInfo.candidateName || 'Candidate' }}</div>
                  <div class="caption grey--text text--darken-1">
                    {{ candidateInfo.interviewDate || 'N/A' }}
                  </div>
                </div>
                <div class="feedback-summary-chips d-flex align-center" v-if="feedbackSummary">
                  <v-chip x-small color="success" outlined class="mr-1" title="Marked correct">
                    <v-icon x-small left>mdi-check-circle</v-icon>
                    {{ feedbackSummary.correct }}
                  </v-chip>
                  <v-chip x-small color="error" outlined class="mr-1" title="Marked incorrect">
                    <v-icon x-small left>mdi-close-circle</v-icon>
                    {{ feedbackSummary.incorrect }}
                  </v-chip>
                  <v-chip x-small color="warning" outlined title="Missing alerts reported">
                    <v-icon x-small left>mdi-flag</v-icon>
                    {{ feedbackSummary.missing }}
                  </v-chip>
                </div>
              </div>
              <div
                class="d-flex flex-wrap align-center candidate-subscores mb-2"
                style="gap: 8px 12px;"
              >
                <div class="d-flex flex-wrap align-center candidate-subscore-line">
                  <span class="caption grey--text text--darken-1 mr-2">Competency</span>
                  <v-chip small dark :color="competencyScoreChipColor" class="font-weight-bold">
                    {{ candidateInfo.competencyScoreLabel || 'N/A' }}
                  </v-chip>
                </div>
                <div class="d-flex flex-wrap align-center candidate-subscore-line">
                  <span class="caption grey--text text--darken-1 mr-2">Cultural</span>
                  <v-chip small dark :color="culturalScoreChipColor" class="font-weight-bold">
                    {{ candidateInfo.culturalScoreLabel || 'N/A' }}
                  </v-chip>
                </div>
              </div>
              <div v-if="candidateInfo.curriculumSummary" class="candidate-about body-2">
                {{ candidateInfo.curriculumSummary }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="candidateInfo.isManualAnalysis" class="manual-metadata-row">
        <v-col cols="12">
          <v-card class="candidate-overlay-card" elevation="2">
            <v-card-text class="pa-4">
              <div class="manual-metadata-first-line">
                <v-chip
                  small
                  dark
                  class="font-weight-bold"
                  :color="headerTotalScoreChipColor"
                >
                  Global match: {{ candidateInfo.headerTotalScoreLabel || 'N/A' }}
                </v-chip>
                <div class="subtitle-2 font-weight-bold manual-candidate-name">
                  {{ candidateInfo.candidateName || 'Candidate' }}
                </div>
                <v-btn
                  v-if="!hideShare"
                  icon
                  small
                  class="ml-auto"
                  :loading="shareLoading"
                  title="Share this analysis"
                  @click="onShareClick"
                >
                  <v-icon small>mdi-share-variant</v-icon>
                </v-btn>
              </div>

              <!-- Share dialog -->
              <v-dialog v-model="shareDialogOpen" max-width="480">
                <v-card>
                  <v-card-title class="subtitle-1 font-weight-bold">Share analysis</v-card-title>
                  <v-card-text>
                    <p class="body-2 mb-3">
                      Anyone with this link can view the analysis for 6 months without logging in.
                    </p>
                    <v-text-field
                      :value="shareUrl"
                      readonly
                      outlined
                      dense
                      hide-details
                      append-icon="mdi-content-copy"
                      @click:append="copyShareUrl"
                    />
                    <div v-if="shareCopied" class="caption success--text mt-1">Copied to clipboard!</div>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="shareDialogOpen = false">Close</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <div class="caption grey--text text--darken-1 mt-2">
                {{ candidateInfo.interviewDate || 'N/A' }} - {{ candidateInfo.analysisDurationLabel || 'N/A' }}
              </div>

              <div
                v-if="candidateInfo.videoDescription"
                class="manual-metadata-description body-2 mt-2"
              >
                {{ candidateInfo.videoDescription }}
              </div>

              <div
                v-if="candidateInfo.curriculumSummary"
                class="manual-metadata-description body-2 mt-2"
              >
                {{ candidateInfo.curriculumSummary }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import AnalysisProgress, {
  isTerminalProgressStatus,
  normalizeProgressStatus,
} from './AnalysisProgress.vue';
import GestureAlertsTimeline from './GestureAlertsTimeline.vue';
import CoherenceSemanticsPanel from './CoherenceSemanticsPanel.vue';
import EngineEmptyState from './runs/EngineEmptyState.vue';
import RunSelector from './runs/RunSelector.vue';
import RunStatusChip from './runs/RunStatusChip.vue';
import RunSummaryCards from './runs/RunSummaryCards.vue';
import RunVersionChips from './runs/RunVersionChips.vue';
import RunVersionDetailPanel from './runs/RunVersionDetailPanel.vue';
import featureFlags from '@/config/featureFlags';
import AnalysisRunsService from '@/services/AnalysisRunsService';
import { analysisKey as buildAnalysisKey, parseAnalysisKey } from '@/store/modules/analysisData';
import { describeActionError } from '@/components/queue/queueFormat';
import { runIdOf } from '@/utils/runLabels';
import { previousSucceededRun } from '@/utils/runSummary';
import {
  normalizeSegmentationTab,
  semanticBlocksForSegmentStrategy,
} from '@/utils/coherenceSemanticsTimeline';
import VideoPlayer from './VideoPlayer.vue';
import socketioService from '@/services/socketioService';
import InterviewAnalysisDataService from '@/services/InterviewAnalysisDataService';
import ShareService from '@/services/ShareService';
import DetectionFeedbackService from '@/services/DetectionFeedbackService';
import IncoherenceService from '@/services/IncoherenceService';

export default {
  name: 'CoherenceAnalysisTab',

  components: {
    AnalysisProgress,
    EngineEmptyState,
    VideoPlayer,
    GestureAlertsTimeline,
    CoherenceSemanticsPanel,
    RunSelector,
    RunStatusChip,
    RunSummaryCards,
    RunVersionChips,
    RunVersionDetailPanel,
  },

  props: {
    /**
     * Store key of the analysis: the composite analysisKey
     * '<sourceType>:<sourceId>:<runId|active>' (M2) or a plain numeric id (legacy callers).
     * Store lookups use it verbatim; API calls use the parsed numeric `sourceId`.
     */
    interviewId: {
      type: [Number, String],
      required: true,
    },
    /** Run block for views without the store (shared page); otherwise read from the store. */
    runInfo: {
      type: Object,
      default: null,
    },
    /** Hide Promote / Pin / Unpin and the run list (shared, read-only views). */
    runControlsReadOnly: {
      type: Boolean,
      default: false,
    },
    analysisData: {
      type: Object,
      default: null,
    },
    candidateInfo: {
      type: Object,
      default: () => ({}),
    },
    hideShare: {
      type: Boolean,
      default: false,
    },
    // Public shared view (/shared/:token): no editing controls such as lie labeling.
    readOnly: {
      type: Boolean,
      default: false,
    },
    // When true, hide coherence-specific UI (semantic panel, per-idea score,
    // lie labeling and timeline markers) while keeping the video, gestures and
    // landmarks. Used to exclude coherence from the CDTI demo scope.
    hideCoherence: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Analysis mode: 'analyzing' | 'replay'
      analysisMode: 'replay',

      // Progress tracking
      isAnalyzing: false,
      analysisProgress: 0,
      analysisCurrentStep: '',
      // Ledger run status/id from the progress stream ('running', 'cancel_requested', ...)
      analysisRunStatus: '',
      analysisRunId: null,
      // Stage labels reported with status 'skipped' in this run (engine off)
      analysisSkippedStages: [],

      // Landmarks data
      landmarksData: [],
      gestureData: [],

      // UI state
      showLandmarks: {
        face: true,
        hands: true,
        gaze: false, // Disabled by default
      },

      // Real-time data
      currentVideoTime: 0,
      currentGestureData: null,
      currentTimestamp: null,
      videoDuration: 0,
      realtimeTranscription: [],

      // Settings
      autoPauseOnRedAlert: false,
      overlaysVisible: true,
      gestureTimelineCollapsed: false,
      nextSemanticaCollapsed: false,
      /** 'idea' | 'none' (grouping off). */
      coherenceSegmentationTab: 'idea',
      /** 'alert_major' | 'computed_subtraction'. */
      coherenceScoreMode: 'computed_subtraction',
      nextSemanticaFontScale: 20 / 24,
      coherencePreferencesHydrated: false,
      coherencePreferencesKeyLoaded: null,
      coherencePreferencesSaveTimer: null,
      uiPreferencesHydrated: false,
      boundVideoElement: null,
      boundVideoMetadataHandler: null,
      videoAspectOrientation: 'horizontal',

      // Share
      shareLoading: false,
      shareDialogOpen: false,
      shareUrl: '',
      shareCopied: false,

      // Feedback dialog timestamp picking
      timestampPickActive: false,

      // Aggregated feedback summary for header chips
      feedbackSummary: { correct: 0, incorrect: 0, missing: 0 },

      // Banner over the video for "Report missing gesture" flow
      reportMissingBannerActive: false,

      // Run history (M2): ledger runs of this source and control action state
      runs: [],
      runsLoading: false,
      runActionBusy: false,
      runActionError: null,
      // Version details panel (F6) and the previous SUCCEEDED run loaded for the delta (F5)
      showVersionDetails: false,
      previousRunLoading: false,

      // Lie label (single range per video), shown/edited from the toolbar
      incoherence: null,
      incoherenceLoading: false,
      incoherenceSaving: false,
      incoherencePickActive: false,
      incoherencePickStartMs: null,
    };
  },

  computed: {
    ...mapGetters('analysisData', [
      'getInterviewVideoSeekSignal',
      'getCoherenceSemaphoreThresholds',
      'getRunInfo',
      'getGestures',
      'getEngineSkipReason',
    ]),

    /** Skip reason when coherence was not run in the viewed run (M3 contract 3), else null. */
    coherenceSkipReason() {
      return this.getEngineSkipReason(this.interviewId, 'coherence');
    },

    /** Parsed store key: { sourceType, sourceId, runId, composite }. */
    analysisKeyParts() {
      return parseAnalysisKey(this.interviewId);
    },

    /** Numeric source id for API calls and socket matching (never the composite key). */
    sourceId() {
      const id = this.analysisKeyParts.sourceId;
      return id == null ? this.interviewId : id;
    },

    /** Run being viewed (`?run=`), null when following the active pointer. */
    runId() {
      return this.analysisKeyParts.runId;
    },

    analysisSourceType() {
      return (
        this.analysisKeyParts.sourceType ||
        (this.getIsManualAnalysis() ? 'manual_analysis' : 'interview')
      );
    },

    /** metadata.run of the analysis being viewed; null for legacy docs without ledger. */
    currentRun() {
      return this.runInfo || this.getRunInfo(this.interviewId);
    },

    runHistoryEnabled() {
      return !!featureFlags.runHistory && !this.runControlsReadOnly;
    },

    /** Gestures payload of the run being viewed (episodes, alerts, semantic modes, snapshot). */
    currentGestures() {
      return this.getGestures(this.interviewId);
    },

    /** Previous SUCCEEDED run of this source (ledger doc from the run list), for the delta. */
    previousRun() {
      if (!this.runHistoryEnabled || !this.currentRun) return null;
      return previousSucceededRun(this.runs, this.currentRun);
    },

    /** Second store key holding the previous run's data (never mixed with the viewed run). */
    previousRunKey() {
      const id = runIdOf(this.previousRun);
      return id ? buildAnalysisKey(this.analysisSourceType, this.sourceId, id) : null;
    },

    previousGestures() {
      return this.previousRunKey ? this.getGestures(this.previousRunKey) : null;
    },

    showRunHeader() {
      return this.runHistoryEnabled || !!this.currentRun;
    },

    interviewVideoSeekSignal() {
      return this.getInterviewVideoSeekSignal(this.interviewId);
    },

    coherenceSemaphoreThresholds() {
      return this.getCoherenceSemaphoreThresholds(this.interviewId);
    },

    analysisStatus() {
      return this.analysisData?.analysisStatus || null;
    },

    videoUrl() {
      return this.analysisData?.videoUrl || null;
    },

    transcription() {
      return this.analysisData?.transcription || [];
    },

    shouldShowLandmarks() {
      return this.landmarksData && this.landmarksData.length > 0;
    },

    landmarksToggleOptions() {
      return {
        face: this.showLandmarks.face,
        hands: this.showLandmarks.hands,
        gaze: this.showLandmarks.gaze,
      };
    },

    headerTotalScoreChipColor() {
      return this.scoreBandVuetifyColor(this.candidateInfo?.headerTotalScorePercent);
    },

    competencyScoreChipColor() {
      return this.scoreBandVuetifyColor(this.candidateInfo?.competencyScorePercent);
    },

    culturalScoreChipColor() {
      return this.scoreBandVuetifyColor(this.candidateInfo?.culturalScorePercent);
    },

    coherenceSemanticModes() {
      return this.analysisData && this.analysisData.coherenceSemanticModes
        ? this.analysisData.coherenceSemanticModes
        : null;
    },

    coherenceAlertsList() {
      return this.analysisData?.coherenceAlerts || [];
    },
    /** Stacked NEXT / gestures / timeline layout on Vuetify small screens and down. */
    isMobileLayout() {
      return Boolean(this.$vuetify?.breakpoint?.smAndDown);
    },
    showMobileNextInVideo() {
      return this.overlaysVisible && this.isMobileLayout && this.videoAspectOrientation === 'vertical';
    },
    showMobileNextBelowVideo() {
      return this.overlaysVisible && this.isMobileLayout && !this.showMobileNextInVideo;
    },
    coherenceTimelineBlocksBySemanticsMode() {
      return semanticBlocksForSegmentStrategy(
        this.coherenceSemanticModes?.diarization,
        this.coherenceSegmentationTab
      );
    },

    /**
     * Timeline markers in coherence mode follow the active segmentation strategy.
     * We derive alert IDs from semantic blocks and map them back to coherence alerts.
     */
    coherenceTimelineAlertsBySemanticsMode() {
      const alerts = Array.isArray(this.coherenceAlertsList) ? this.coherenceAlertsList : [];
      const blocks = this.coherenceTimelineBlocksBySemanticsMode;

      if (!Array.isArray(blocks) || blocks.length === 0) {
        return [];
      }

      const alertById = new Map();
      alerts.forEach((a) => {
        const id = a?.alert_id ?? a?.alertId;
        if (id != null) {
          const k = String(id);
          if (!alertById.has(k)) alertById.set(k, a);
        }
      });

      const selectedIds = new Set();
      blocks.forEach((b) => {
        const rawIds = b?.contributing_alert_ids ?? b?.contributingAlertIds;
        if (!Array.isArray(rawIds)) return;
        rawIds.forEach((id) => {
          if (id != null) selectedIds.add(String(id));
        });
      });

      if (selectedIds.size === 0) {
        return [];
      }

      return alerts.filter((a) => {
        const id = a?.alert_id ?? a?.alertId;
        return id != null && selectedIds.has(String(id));
      });
    },

    /** Source type for the lie label API, mirroring DetectionFeedbackService usage. */
    incoherenceSourceType() {
      return this.candidateInfo?.isManualAnalysis ? 'manual_analysis' : 'interview';
    },

    /** Range passed to the player timeline overlay; null when no lie is labeled. */
    incoherenceRangeMs() {
      const startMs = Number(this.incoherence?.incoherence_start_ms);
      const endMs = Number(this.incoherence?.incoherence_end_ms);
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        return null;
      }
      return { startMs, endMs };
    },

    /** 'incoherence' | 'truthful' | null; rows without label_type predate the column and are lies. */
    incoherenceLabelType() {
      if (!this.incoherence) return null;
      return this.incoherence.label_type === 'truthful' ? 'truthful' : 'incoherence';
    },

    incoherencePickHint() {
      return this.incoherencePickStartMs == null
        ? 'Click the timeline to set the lie start'
        : 'Click the timeline to set the lie end';
    },

  },

  watch: {
    interviewVideoSeekSignal: {
      handler(sig) {
        if (!sig || !Number.isFinite(sig.seconds)) return;
        this.$nextTick(() => this.onJumpToTime(sig.seconds));
      },
    },
    analysisData: {
      immediate: true,
      handler(newData) {
        if (newData) {
          // Load gesture data
          this.gestureData = newData.gestureAnalysis || [];

          // Load landmarks data
          this.landmarksData = newData.landmarksData || [];

          // Add initial log
          if (this.gestureData.length > 0) {
            this.addLog('info', `Loaded ${this.gestureData.length} gesture entries`);
          }

          if (this.landmarksData.length > 0) {
            this.addLog('info', `Loaded ${this.landmarksData.length} landmark frames`);
          }

          const va = newData.coherenceAlerts;
          if (Array.isArray(va) && va.length > 0) {
            this.addLog('info', `Loaded ${va.length} coherence alerts`);
          }

          this.ensureCoherencePreferencesLoaded();
          this.scheduleVideoMetadataBinding();
        }
      },
    },
    coherenceSegmentationTab(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.scheduleSaveCoherencePreferences();
    },
    coherenceScoreMode(newValue, oldValue) {
      if (newValue === oldValue) return;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('vp.coherenceEvaluation.scoreMode', newValue);
      }
      this.scheduleSaveCoherencePreferences();
    },
    nextSemanticaFontScale(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.scheduleSaveCoherencePreferences();
    },
    autoPauseOnRedAlert() {
      this.saveOverlayUiPreferences();
    },
    'showLandmarks.face'() {
      this.saveOverlayUiPreferences();
    },
    'showLandmarks.hands'() {
      this.saveOverlayUiPreferences();
    },
    gestureTimelineCollapsed() {
      this.saveOverlayUiPreferences();
    },
    nextSemanticaCollapsed() {
      this.saveOverlayUiPreferences();
    },
    videoUrl() {
      this.unbindVideoMetadataListeners();
      this.scheduleVideoMetadataBinding();
    },
    isMobileLayout(next) {
      if (next) {
        this.gestureTimelineCollapsed = false;
        this.nextSemanticaCollapsed = false;
      }
    },
    incoherenceSourceType() {
      // Reload when the manual/interview flag hydrates after mount.
      this.loadIncoherence();
    },
    sourceId() {
      this.loadRuns();
    },

    previousRunKey: {
      immediate: true,
      handler() {
        this.loadPreviousRun();
      },
    },
  },

  mounted() {
    if (this.isMobileLayout) {
      this.gestureTimelineCollapsed = false;
      this.nextSemanticaCollapsed = false;
    }
    this.loadFeedbackSummary();
    this.loadIncoherence();
    this.loadRuns();

    // Check if analysis is in progress
    if (this.analysisStatus === 'in_progress' || this.analysisStatus === 'ANALYSING') {
      this.isAnalyzing = true;
      this.analysisProgress = this.analysisData?.analysisProgress || 0;
      this.addLog('info', 'Analysis in progress...');
    } else if (this.analysisStatus === 'completed' || this.analysisStatus === 'COMPLETED') {
      this.addLog('info', 'Analysis completed, replay mode active');
    }

    // Ensure Socket.IO connection is established
    this.setupSocketConnection();

    // Listen for analysis progress updates via WebSocket
    socketioService.emisor.$on('analysisProgressUpdate', this.onAnalysisProgress);
    socketioService.emisor.$on('realtimeUpdate', this.onRealtimeUpdate);
    socketioService.emisor.$on('transcriptionUpdate', this.onTranscriptionUpdate);
    this.ensureCoherencePreferencesLoaded();
    this.loadOverlayUiPreferences();
    this.scheduleVideoMetadataBinding();

  },

  beforeDestroy() {
    // Clean up event listeners
    socketioService.emisor.$off('analysisProgressUpdate', this.onAnalysisProgress);
    socketioService.emisor.$off('realtimeUpdate', this.onRealtimeUpdate);
    socketioService.emisor.$off('transcriptionUpdate', this.onTranscriptionUpdate);
    if (this.coherencePreferencesSaveTimer) {
      clearTimeout(this.coherencePreferencesSaveTimer);
      this.coherencePreferencesSaveTimer = null;
    }
    document.removeEventListener('keydown', this.onIncoherencePickKeydown);
    this.unbindVideoMetadataListeners();
  },

  methods: {
    async loadRuns() {
      if (!this.runHistoryEnabled || !this.sourceId) return;
      this.runsLoading = true;
      try {
        const response = await AnalysisRunsService.list({
          source_type: this.analysisSourceType,
          source_id: this.sourceId,
          limit: 50,
        });
        const payload = response?.data?.data ?? response?.data ?? {};
        this.runs = Array.isArray(payload.items) ? payload.items : Array.isArray(payload) ? payload : [];
      } catch (error) {
        console.error('[CoherenceAnalysisTab] Failed to load runs', error);
        this.runs = [];
      } finally {
        this.runsLoading = false;
      }
    },

    onSelectRun(runId) {
      this.$emit('select-run', runId);
    },

    /** Loads the previous SUCCEEDED run's gestures into its own store key (delta + config diff). */
    async loadPreviousRun() {
      const key = this.previousRunKey;
      if (!key || this.getGestures(key)) return;
      this.previousRunLoading = true;
      try {
        await this.$store.dispatch('analysisData/fetchGestures', {
          analysisId: key,
          isManual: this.analysisSourceType === 'manual_analysis',
        });
      } catch (error) {
        console.error('[CoherenceAnalysisTab] Failed to load previous run', error);
      } finally {
        this.previousRunLoading = false;
      }
    },

    async runControlAction(action, run) {
      const runId = runIdOf(run);
      if (!runId || this.runActionBusy) return;
      this.runActionBusy = true;
      this.runActionError = null;
      try {
        let queued = null;
        if (action === 'promote') await AnalysisRunsService.promote(runId);
        else if (action === 'pin') await AnalysisRunsService.pin(runId);
        else if (action === 'unpin') await AnalysisRunsService.unpin(runId);
        else if (action === 'replay') {
          const response = await AnalysisRunsService.replay(runId);
          queued = response?.data ?? null;
        }
        await this.loadRuns();
        this.$emit('run-changed', { action, runId, queued });
      } catch (error) {
        this.runActionError = describeActionError(error, action);
      } finally {
        this.runActionBusy = false;
      }
    },

    onPromoteRun(run) {
      return this.runControlAction('promote', run);
    },

    onPinRun(run) {
      return this.runControlAction('pin', run);
    },

    onUnpinRun(run) {
      return this.runControlAction('unpin', run);
    },

    /** Replay (M5 X2): queues a new run with this one's configuration and engines. */
    onReplayRun(run) {
      return this.runControlAction('replay', run);
    },

    async onShareClick() {
      this.shareLoading = true;
      try {
        const response = await ShareService.createShareToken(
          'manual_analysis',
          this.sourceId,
          this.runId
        );
        const token = response.data.data.token;
        this.shareUrl = `${window.location.origin}/shared/${token}`;
        this.shareCopied = false;
        this.shareDialogOpen = true;
      } catch (e) {
        console.error('[Share] Failed to create share token', e);
      } finally {
        this.shareLoading = false;
      }
    },

    async copyShareUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.shareCopied = true;
        setTimeout(() => { this.shareCopied = false; }, 3000);
      } catch (e) {
        console.error('[Share] Failed to copy URL', e);
      }
    },

    getIsManualAnalysis() {
      return !!(
        this.analysisData?.isManualAnalysis ||
        this.candidateInfo?.isManualAnalysis
      );
    },

    async ensureCoherencePreferencesLoaded() {
      if (!this.sourceId) return;
      const isManual = this.getIsManualAnalysis();
      const key = `${this.interviewId}:${isManual ? 'manual' : 'regular'}`;
      if (this.coherencePreferencesKeyLoaded === key && this.coherencePreferencesHydrated) {
        return;
      }

      this.coherencePreferencesHydrated = false;

      try {
        const response = await InterviewAnalysisDataService.getCoherencePreferences(
          this.sourceId,
          isManual
        );
        const prefs = response?.data?.data || {};
        // Ideas are the only grouping: stored 'merged' / 'per_alert' preferences
        // read as 'idea'; 'none' (grouping off) is kept.
        const segmentationTab = normalizeSegmentationTab(prefs.segmentationTab);
        const lsScoreMode = (typeof localStorage !== 'undefined' && localStorage.getItem('vp.coherenceEvaluation.scoreMode')) || null;
        const scoreMode =
          prefs.scoreMode === 'alert_major' || prefs.scoreMode === 'computed_subtraction'
            ? prefs.scoreMode
            : (lsScoreMode === 'alert_major' || lsScoreMode === 'computed_subtraction')
              ? lsScoreMode
              : 'computed_subtraction';
        const fontScale = Number(prefs.fontScale);

        this.coherenceSegmentationTab = segmentationTab;
        this.coherenceScoreMode = scoreMode;
        this.nextSemanticaFontScale = Number.isFinite(fontScale)
          ? Math.max(16 / 24, Math.min(24 / 24, fontScale))
          : 20 / 24;
      } catch (error) {
        // Keep defaults on any retrieval error.
      } finally {
        this.coherencePreferencesKeyLoaded = key;
        this.coherencePreferencesHydrated = true;
      }
    },

    scheduleSaveCoherencePreferences() {
      if (!this.coherencePreferencesHydrated) return;
      if (!this.sourceId) return;

      if (this.coherencePreferencesSaveTimer) {
        clearTimeout(this.coherencePreferencesSaveTimer);
      }

      this.coherencePreferencesSaveTimer = setTimeout(() => {
        this.saveCoherencePreferences();
      }, 350);
    },

    async saveCoherencePreferences() {
      if (!this.coherencePreferencesHydrated || !this.sourceId) return;

      try {
        await InterviewAnalysisDataService.setCoherencePreferences(
          this.sourceId,
          {
            segmentationTab: this.coherenceSegmentationTab,
            scoreMode: this.coherenceScoreMode,
            fontScale: this.nextSemanticaFontScale,
          },
          this.getIsManualAnalysis()
        );
      } catch (error) {
        // Do not block UX if saving fails.
      }
    },

    setupSocketConnection() {
      // Check if socket connection already exists
      if (!Vue.prototype.$socket) {
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            const userId = user.id || user.userId;
            if (userId) {
              Vue.prototype.$socket = socketioService.setupSocketConnection(userId, null, true);
            }
          }
        } catch (error) {
          console.error('Error setting up Socket.IO connection:', error);
        }
      }
    },

    onAnalysisProgress(data) {
      // Handle both interview_id and manual_analysis_id
      const dataId = data.interviewId || data.manual_analysis_id;
      if (dataId !== this.sourceId) return;

      this.analysisProgress = data.progress || 0;
      this.analysisCurrentStep = data.currentStep || '';
      const status = normalizeProgressStatus(data.status);
      if (status === 'skipped') {
        // Per-stage event (engine not requested): remember the stage, keep the run status.
        const stage = data.currentStep || data.stage || '';
        if (stage && !this.analysisSkippedStages.includes(stage)) {
          this.analysisSkippedStages = [...this.analysisSkippedStages, stage];
        }
      } else if (status) {
        this.analysisRunStatus = status;
      }
      const runId = data.run_id || data.runId || data.job_id || data.jobId;
      if (runId) this.analysisRunId = String(runId);

      // Add log
      this.addLog('progress', data.currentStep);

      // Update analysis state: subtype (legacy) or terminal ledger status
      const terminal = isTerminalProgressStatus(status) || data.subtype === 'analysis_complete';
      if (!terminal && data.subtype === 'analysis_started') {
        this.isAnalyzing = true;
        this.analysisSkippedStages = [];
        this.addLog('info', 'Analysis started');
      } else if (terminal) {
        this.isAnalyzing = false;
        if (status === 'failed') {
          this.addLog('error', 'Analysis failed');
        } else if (status === 'cancelled') {
          this.addLog('info', 'Analysis cancelled');
        } else {
          this.addLog('info', 'Analysis completed successfully');
          // Refresh data to get complete analysis
          this.$emit('analysis-complete');
        }
        this.$emit('analysis-finished', {
          status: status || 'completed',
          runId: this.analysisRunId,
        });
        this.analysisRunStatus = '';
        this.analysisRunId = null;
        this.analysisSkippedStages = [];
      }
    },

    onCancelRequested() {
      // Optimistic: the worker confirms through the progress stream ('cancel_requested' / 'cancelled').
      this.analysisRunStatus = 'cancel_requested';
      this.addLog('info', 'Cancel requested');
    },

    onRealtimeUpdate(data) {
      // Handle both interview_id and manual_analysis_id
      const dataId = data.analysisId || data.analysis_id;
      if (dataId !== this.sourceId) {
        return;
      }

      // Update progress
      if (data.progressPercent !== undefined) {
        this.analysisProgress = Math.round(data.progressPercent);
      }

      // Update current step
      if (data.step) {
        this.analysisCurrentStep = data.step;
      }

      // Update video timestamp if provided and video is loaded
      if (data.video_timestamp !== undefined && this.$refs.videoPlayer) {
        const video = this.$refs.videoPlayer.$refs.videoElement;
        if (video && video.readyState >= 2) {
          // HAVE_CURRENT_DATA or better
          const targetTime = data.video_timestamp;
          const currentTime = video.currentTime;

          // Only update if difference is significant (>0.5s) to avoid constant seeking
          if (Math.abs(currentTime - targetTime) > 0.5) {
            video.currentTime = targetTime;
            this.addLog('debug', `Video seeked to ${targetTime.toFixed(2)}s`);
          }
        }
      }

      // Add landmarks if provided (for real-time visualization)
      if (data.landmarks) {
        // Landmarks are handled by VideoLandmarksPlayer component
        // This is just for logging
        this.addLog('debug', `Landmarks updated @ ${(data.video_timestamp || 0).toFixed(2)}s`);
      }

      // Add log for progress updates (throttled to avoid spam)
      if (data.progressPercent !== undefined && data.progressPercent % 10 < 1) {
        this.addLog(
          'progress',
          `${data.step || 'Processing'} - ${data.progressPercent.toFixed(1)}%`
        );
      }
    },

    onTranscriptionUpdate(data) {
      // Handle real-time transcription updates
      const dataId = data.analysisId || data.analysis_id || data.interviewId;
      if (dataId !== this.sourceId) return;

      if (data.transcription) {
        // Add new transcription entry
        if (Array.isArray(data.transcription)) {
          this.realtimeTranscription = [...this.realtimeTranscription, ...data.transcription];
        } else {
          this.realtimeTranscription.push(data.transcription);
        }
      }

    },

    onVideoTimeUpdate(time) {
      this.currentVideoTime = time;
      this.currentTimestamp = this.formatTime(time);

      // Find gesture data for current time
      const previousGesture = this.currentGestureData;
      this.currentGestureData = this.findGestureAtTime(time);

      // Log gesture JSON when a new gesture is detected
      if (this.currentGestureData && this.currentGestureData !== previousGesture) {
        this.addLog('gesture', this.currentGestureData, true); // true = isJson
      }

      // Update video duration if available
      if (this.$refs.videoPlayer && this.$refs.videoPlayer.$refs.videoElement) {
        const video = this.$refs.videoPlayer.$refs.videoElement;
        if (video.duration && isFinite(video.duration)) {
          this.videoDuration = video.duration;
        }
      }
    },

    getVideoElement() {
      return this.$refs.videoPlayer?.$refs?.videoElement || null;
    },

    syncVideoTimingFromElement() {
      const video = this.getVideoElement();
      if (!video) return;
      this.updateVideoAspectOrientation(video);
      if (Number.isFinite(video.duration) && video.duration > 0) {
        this.videoDuration = Number(video.duration);
      }
      if (Number.isFinite(video.currentTime) && video.currentTime >= 0) {
        this.currentVideoTime = Number(video.currentTime);
        this.currentTimestamp = this.formatTime(this.currentVideoTime);
      }
    },

    bindVideoMetadataListeners() {
      const video = this.getVideoElement();
      if (!video) return false;
      if (this.boundVideoElement === video && this.boundVideoMetadataHandler) {
        this.syncVideoTimingFromElement();
        return true;
      }

      this.unbindVideoMetadataListeners();
      const handler = () => this.syncVideoTimingFromElement();
      video.addEventListener('loadedmetadata', handler);
      video.addEventListener('durationchange', handler);
      video.addEventListener('loadeddata', handler);
      video.addEventListener('canplay', handler);

      this.boundVideoElement = video;
      this.boundVideoMetadataHandler = handler;
      this.syncVideoTimingFromElement();
      return true;
    },

    unbindVideoMetadataListeners() {
      const video = this.boundVideoElement;
      const handler = this.boundVideoMetadataHandler;
      if (video && handler) {
        video.removeEventListener('loadedmetadata', handler);
        video.removeEventListener('durationchange', handler);
        video.removeEventListener('loadeddata', handler);
        video.removeEventListener('canplay', handler);
      }
      this.boundVideoElement = null;
      this.boundVideoMetadataHandler = null;
    },

    scheduleVideoMetadataBinding() {
      this.$nextTick(() => {
        if (this.bindVideoMetadataListeners()) return;
        requestAnimationFrame(() => {
          this.bindVideoMetadataListeners();
        });
      });
    },

    updateVideoAspectOrientation(videoEl) {
      const w = Number(videoEl?.videoWidth);
      const h = Number(videoEl?.videoHeight);
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return;
      this.videoAspectOrientation = h > w ? 'vertical' : 'horizontal';
    },

    onJumpToTime(seconds) {
      if (this.$refs.videoPlayer && this.$refs.videoPlayer.$refs.videoElement) {
        const video = this.$refs.videoPlayer.$refs.videoElement;
        if (video && isFinite(seconds) && !isNaN(seconds)) {
          video.currentTime = seconds;
        }
      }
    },

    async loadFeedbackSummary() {
      if (!this.sourceId) return;
      const sourceType = this.candidateInfo?.isManualAnalysis ? 'manual_analysis' : 'interview';
      try {
        const { data } = await DetectionFeedbackService.list(sourceType, this.sourceId, this.runId);
        this.feedbackSummary = data?.data?.summary ?? { correct: 0, incorrect: 0, missing: 0 };
      } catch (e) {
        // silent
      }
    },

    onReportMissingSaved() {
      this.reportMissingBannerActive = false;
      this.loadFeedbackSummary();
    },

    /** Mirror of startIncoherenceEdit(): the two pick modes are mutually exclusive. */
    onReportMissingStart() {
      this.cancelIncoherencePick();
      this.reportMissingBannerActive = true;
    },

    onVideoPickTimestamp(seconds) {
      if (this.incoherencePickActive) {
        this.onIncoherencePickTimestamp(seconds);
        return;
      }
      if (!this.reportMissingBannerActive) return;
      const timeline = this.$refs.gestureAlertsTimeline;
      if (timeline && typeof timeline.captureMissingTimestamp === 'function') {
        timeline.captureMissingTimestamp(seconds);
      }
    },

    cancelReportMissing() {
      // Inform the timeline to drop its internal active state.
      this.reportMissingBannerActive = false;
      // The timeline owns its state, but the user only sees the banner — most
      // common reason for cancelling is hitting the banner button, so we
      // broadcast via $root so any GestureAlertsTimeline can react.
      this.$root.$emit('vp:cancel-report-missing');
    },

    async loadIncoherence() {
      if (!this.sourceId) return;
      this.incoherenceLoading = true;
      try {
        const { data } = await IncoherenceService.get(this.incoherenceSourceType, this.sourceId);
        this.incoherence = data?.incoherence || null;
      } catch (e) {
        // silent — lie labeling is an auxiliary evaluation tool
      } finally {
        this.incoherenceLoading = false;
      }
    },

    startIncoherenceEdit() {
      if (this.reportMissingBannerActive) this.cancelReportMissing();
      this.incoherencePickStartMs = null;
      this.incoherencePickActive = true;
      document.addEventListener('keydown', this.onIncoherencePickKeydown);
    },

    cancelIncoherencePick() {
      this.incoherencePickActive = false;
      this.incoherencePickStartMs = null;
      document.removeEventListener('keydown', this.onIncoherencePickKeydown);
    },

    onIncoherencePickKeydown(event) {
      if (event && event.key === 'Escape') this.cancelIncoherencePick();
    },

    /** Two-click pick: first timeline click = lie start, second = end (swapped if reversed). */
    onIncoherencePickTimestamp(seconds) {
      const ms = Math.round(Number(seconds) * 1000);
      if (!Number.isFinite(ms) || ms < 0) return;
      if (this.incoherencePickStartMs == null) {
        this.incoherencePickStartMs = ms;
        return;
      }
      const startMs = Math.min(this.incoherencePickStartMs, ms);
      const endMs = Math.max(this.incoherencePickStartMs, ms);
      if (endMs === startMs) return; // need a non-empty range; keep waiting for the end click
      this.cancelIncoherencePick();
      this.saveIncoherenceRange(startMs, endMs);
    },

    async saveIncoherenceRange(startMs, endMs) {
      if (!this.sourceId) return;
      this.incoherenceSaving = true;
      try {
        const { data } = await IncoherenceService.upsert(this.incoherenceSourceType, this.sourceId, {
          incoherence_start_ms: startMs,
          incoherence_end_ms: endMs,
        });
        this.incoherence = data?.incoherence || { incoherence_start_ms: startMs, incoherence_end_ms: endMs };
      } catch (e) {
        console.error('[Lie] Failed to save lie label', e);
      } finally {
        this.incoherenceSaving = false;
      }
    },

    /** Truthful label: the video is asserted to have no lie (replaces any lie range). */
    async markIncoherenceTruthful() {
      if (!this.sourceId || this.incoherenceSaving) return;
      this.cancelIncoherencePick();
      this.incoherenceSaving = true;
      try {
        const { data } = await IncoherenceService.upsert(this.incoherenceSourceType, this.sourceId, {
          label_type: 'truthful',
        });
        this.incoherence = data?.incoherence || { label_type: 'truthful', incoherence_start_ms: null, incoherence_end_ms: null };
      } catch (e) {
        console.error('[Lie] Failed to mark the video as truthful', e);
      } finally {
        this.incoherenceSaving = false;
      }
    },

    async clearIncoherence() {
      if (!this.sourceId || this.incoherenceSaving) return;
      this.incoherenceSaving = true;
      try {
        await IncoherenceService.remove(this.incoherenceSourceType, this.sourceId);
        this.incoherence = null;
      } catch (e) {
        console.error('[Lie] Failed to clear lie label', e);
      } finally {
        this.incoherenceSaving = false;
      }
    },

    onPickTimestamp(seconds) {
      if (!this.timestampPickActive) return;
      const panel = this.$refs.coherenceSemanticsPanel;
      if (panel && typeof panel.receiveTimelineTimestamp === 'function') {
        panel.receiveTimelineTimestamp(Math.round(Number(seconds) * 1000));
      }
      this.timestampPickActive = false;
    },

    onToggleOverlays() {
      this.overlaysVisible = !this.overlaysVisible;
    },

    onFacialLandmarksSwitch(enabled) {
      this.showLandmarks.face = !!enabled;
      this.onLandmarksToggleChange();
    },

    onHandLandmarksSwitch(enabled) {
      this.showLandmarks.hands = !!enabled;
      this.onLandmarksToggleChange();
    },

    onPauseOnRedAlertsSwitch(enabled) {
      this.autoPauseOnRedAlert = !!enabled;
    },

    loadOverlayUiPreferences() {
      const key = 'coherence_analysis_overlay_ui_v1';
      try {
        const raw = localStorage.getItem(key);
        if (!raw) {
          this.uiPreferencesHydrated = true;
          return;
        }
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          if (typeof parsed.faceLandmarks === 'boolean') this.showLandmarks.face = parsed.faceLandmarks;
          if (typeof parsed.handLandmarks === 'boolean') this.showLandmarks.hands = parsed.handLandmarks;
          if (typeof parsed.pauseOnRedAlerts === 'boolean') this.autoPauseOnRedAlert = parsed.pauseOnRedAlerts;
          if (typeof parsed.gestureTimelineCollapsed === 'boolean') {
            this.gestureTimelineCollapsed = parsed.gestureTimelineCollapsed;
          }
          if (typeof parsed.nextSemanticaCollapsed === 'boolean') {
            this.nextSemanticaCollapsed = parsed.nextSemanticaCollapsed;
          }
        }
      } catch (error) {
        // Ignore broken local preferences.
      } finally {
        this.uiPreferencesHydrated = true;
      }
    },

    saveOverlayUiPreferences() {
      if (!this.uiPreferencesHydrated) return;
      const key = 'coherence_analysis_overlay_ui_v1';
      const payload = {
        faceLandmarks: !!this.showLandmarks.face,
        handLandmarks: !!this.showLandmarks.hands,
        pauseOnRedAlerts: !!this.autoPauseOnRedAlert,
        gestureTimelineCollapsed: !!this.gestureTimelineCollapsed,
        nextSemanticaCollapsed: !!this.nextSemanticaCollapsed,
      };
      try {
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (error) {
        // Ignore storage quota/privacy failures.
      }
    },

    findGestureAtTime(seconds) {
      if (!this.gestureData || this.gestureData.length === 0) return null;

      // Find the gesture entry closest to current time
      // Gesture data is every 500ms, so find the nearest one
      let closestGesture = null;
      let minDiff = Infinity;

      for (const gesture of this.gestureData) {
        const gestureSeconds = this.parseTimeToSeconds(gesture.time);
        const diff = Math.abs(gestureSeconds - seconds);

        if (diff < minDiff) {
          minDiff = diff;
          closestGesture = gesture;
        }

        // If we've passed it, no need to continue
        if (gestureSeconds > seconds) break;
      }

      return closestGesture;
    },

    parseTimeToSeconds(timeString) {
      if (!timeString) return 0;
      const parts = timeString.split(':');
      if (parts.length !== 3) return 0;

      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      const milliseconds = parseInt(parts[2], 10) || 0;

      return minutes * 60 + seconds + milliseconds / 1000;
    },

    formatTime(seconds) {
      if (!seconds || !isFinite(seconds) || isNaN(seconds)) {
        return '00:00:000';
      }

      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      const ms = Math.floor((seconds % 1) * 1000);

      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
    },

    addLog() {
      // Developer JSON console removed; hook kept for socket handlers
    },

    /** Vuetify color names for chips/avatar: >80 success, 50–80 warning, <50 error */
    scoreBandVuetifyColor(p) {
      if (p == null || Number.isNaN(Number(p))) {
        return 'grey darken-2';
      }
      const n = Number(p);
      if (n > 80) return 'success';
      if (n >= 50) return 'warning';
      return 'error';
    },

    onLandmarksToggle() {
      // This method is called when landmarks toggles change
      // The video player will react to the prop changes automatically
    },

    onLandmarksToggleChange() {
      // Update video player landmarks visibility if needed
      if (this.$refs.videoPlayer) {
        // The VideoLandmarksPlayer will read showLandmarks from props
        this.$forceUpdate();
      }
    },

  },
};
</script>

<style scoped>
.report-missing-banner {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 5;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.coherence-analysis-tab {
  overflow: visible;
}

/* Unified subtle shadow for all cards */
.coherence-analysis-tab >>> .v-card {
  box-shadow: 0 2px 6px -1px rgba(0, 0, 0, 0.12), 0 4px 8px -1px rgba(0, 0, 0, 0.1) !important;
}

.coherence-analysis-tab >>> .v-row {
  overflow: visible;
}

.main-layout-row {
  display: flex;
  align-items: stretch;
  overflow: visible;
}

.coherence-in-video-overlays {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 8px;
  pointer-events: none;
  box-sizing: border-box;
  gap: 8px;
}

.coherence-in-video-overlays.coherence-in-video-overlays--manual {
  justify-content: space-between;
}

.coherence-in-video-overlays .vio-col-left {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  width: 300px;
  min-width: 300px;
  max-width: 300px;
  flex: 0 0 300px;
  min-height: 0;
  overflow: hidden;
}

.coherence-in-video-overlays .vio-col-left >>> .gesture-alerts-card {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  height: 100%;
}

.coherence-in-video-overlays .vio-col-left.vio-col-left--collapsed {
  align-self: flex-start;
  overflow: visible;
}

.coherence-in-video-overlays .vio-col-left.vio-col-left--collapsed >>> .gesture-alerts-card {
  flex: 0 0 auto !important;
  height: auto !important;
  max-height: none !important;
  min-height: unset !important;
}

.coherence-in-video-overlays .vio-col-right {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  gap: 8px;
  max-width: min(360px, 42vw);
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
}

/* NEXT Semantica fills remaining height under the semaphore */
.coherence-in-video-overlays .vio-col-right > *:first-child {
  flex-shrink: 0;
}

.coherence-in-video-overlays .vio-col-right >>> .coherence-semantics-card {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  height: 100%;
}

.coherence-in-video-overlays .vio-col-right.vio-col-right--collapsed {
  align-self: flex-start;
  height: auto;
  max-height: none;
  min-height: unset;
  overflow: visible;
}

.coherence-in-video-overlays .vio-col-right.vio-col-right--collapsed >>> .coherence-semantics-card {
  flex: 0 0 auto !important;
  height: auto !important;
  max-height: none !important;
  min-height: unset !important;
}

.coherence-in-video-overlays .vio-bottom-left {
  position: absolute;
  left: 8px;
  bottom: 8px;
  width: 300px;
  max-width: min(300px, calc(100% - 16px));
  max-height: 42%;
  pointer-events: auto;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.coherence-in-video-overlays >>> .v-card:not(.coherence-semantics-card) {
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  background: rgba(105, 105, 105, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: auto !important;
  min-height: unset !important;
  max-height: none !important;
}

/* NEXT Semantica: stay within the overlay column (flex parent), not unbounded by generic overlay card rules */
.coherence-in-video-overlays .vio-col-right >>> .coherence-semantics-card.v-card {
  --semantics-panel-surface-rgb: 105, 105, 105;
  --semantics-panel-surface-alpha: 0.5;
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  background: rgba(105, 105, 105, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 0 !important;
  max-height: 100% !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

.coherence-in-video-overlays .vio-col-right.vio-col-right--collapsed >>> .coherence-semantics-card.v-card {
  height: auto !important;
  max-height: none !important;
  min-height: unset !important;
}

.candidate-overlay-card {
  backdrop-filter: blur(3px);
  background: rgba(255, 255, 255, 0.95);
}

.candidate-about {
  overflow: visible;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
}

.nonverbal-overlay-card {
  overflow: hidden;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
}

.coherence-in-video-overlays .vio-col-right >>> .truth-indicator-card {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
}

.coherence-video-card {
  background-color: #2a2a2a !important;
  position: relative;
}

.main-layout-row .v-col {
  padding: 8px;
  overflow: visible;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.coherence-analysis-tab .main-layout-row .coherence-main-col.v-col {
  padding: 4px 6px;
}

.manual-metadata-row {
  margin-top: 4px;
}

.manual-metadata-first-line {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.manual-candidate-name {
  min-width: 0;
  word-break: break-word;
}

.manual-metadata-description {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
}

.coherence-external-panels .coherence-external-semantics-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
}

.coherence-external-panels >>> .coherence-semantics-card {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  height: 100%;
}

.left-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}

.right-column {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  height: 100%;
}

.video-card {
  overflow: hidden;
}

.coherence-video-card.video-card {
  overflow: visible;
}

.coherence-main-col .video-container-wrapper {
  max-height: none;
}

.video-container-wrapper {
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  min-height: clamp(560px, 78vh, 1280px);
  max-height: none;
  overflow: visible;
}

.video-container-wrapper >>> .video-landmarks-player,
.video-container-wrapper >>> .video-container {
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: none;
  height: auto;
}

.video-container-wrapper >>> .video-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-height: none;
  overflow: visible;
}

.video-container-wrapper >>> .interview-video {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center center;
}

.right-column >>> .truth-coherence-card,
.right-column >>> .truth-indicator-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  flex: 1 1 auto;
}

.right-column >>> .v-card {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1 1 auto;
}

.right-column >>> .v-card-text {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
}

@media (max-width: 959px) {
  .coherence-analysis-tab .main-layout-row .coherence-main-col.v-col {
    padding: 2px 0;
  }

  /* Tablet / narrow md: taller video stage */
  .video-container-wrapper {
    min-height: clamp(500px, 78vh, 860px);
  }

  .coherence-mobile-next-in-video-overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 8px;
    pointer-events: none;
    z-index: 6;
    display: flex;
    align-items: flex-end;
  }

  .coherence-mobile-next-in-video-overlay .coherence-mobile-next-stack {
    width: 100%;
    pointer-events: auto;
  }

  .coherence-video-card.video-card {
    width: 100%;
    max-width: 100%;
    margin: 0;
  }

  .video-container-wrapper >>> .video-landmarks-player,
  .video-container-wrapper >>> .video-container,
  .video-container-wrapper >>> .player-shell,
  .video-container-wrapper >>> .video-wrapper {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
  }

  .video-container-wrapper >>> .interview-video {
    width: 100% !important;
    max-width: 100% !important;
  }

  .coherence-mobile-next-stack >>> .coherence-semantics-card.v-card {
    min-width: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
    max-height: none !important;
    flex: 0 0 auto !important;
    min-height: 0 !important;
    overflow: hidden;
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    background: rgba(105, 105, 105, 0.5) !important;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .coherence-mobile-gestures-chrome >>> .gesture-alerts-card.v-card {
    min-width: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
    background: rgba(105, 105, 105, 0.5) !important;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .manual-metadata-first-line {
    gap: 8px;
  }
}

/* xs-only (<600px): keep the stage tall enough for mobile readability */
@media (max-width: 599px) {
  .video-container-wrapper {
    min-height: clamp(390px, 80vh, 760px);
  }

  .video-container-wrapper >>> .interview-video {
    object-fit: cover !important;
    object-position: center center !important;
  }
}
</style>
